import { api } from "../api";
import { platformLabel } from "../platforms";

// Rewritten 2026-09-03 (same UI/UX pass as every other page tonight). What
// changed and why:
//
// - Raw platform slugs ("google_business_sites") shown verbatim — same fix
//   as everywhere else, via the shared platformLabel() helper.
// - Real bug: `task_type.replace("_", " ")` only replaces the FIRST
//   underscore (no /g flag), so "submit_business_listing" rendered as
//   "submit business_listing" — visibly broken, not cosmetic. Fixed.
// - `due_at` has existed on assisted_cards since Phase 1 and the backend
//   already sorts by it, but the date itself was never shown anywhere in
//   this UI — no way to tell which task is actually overdue vs. just
//   queued. Now shown, in red if the date has passed.
// - The backend already returns cards `ORDER BY platform, due_at` — sorted
//   for exactly this purpose but never grouped visually. Now rendered as
//   platform sections with counts, so a VA (or Kalraj) can batch every Yelp
//   card in one sitting instead of hunting through a flat mixed list.
// - Added a search box, same self-hiding-under-N-items pattern as Fleet.
//
// Deliberately NOT collapsed into accordions the way Connections was: these
// are active to-do items you need the fields visible to work through (copy
// each value, paste it into the actual platform, come back and paste the
// result URL) — collapsing them would get in the way of the one thing this
// page exists to help you do, not reduce clutter.
export async function assistedView(root: HTMLElement) {
  root.innerHTML = `<div class="topbar"><h1>Assisted queue</h1></div><div id="content">Loading…</div>`;
  const content = root.querySelector("#content")!;

  async function load() {
    const [{ cards }, { clients }] = await Promise.all([
      api<{ cards: any[] }>("/v1/assisted"),
      api<{ clients: any[] }>("/v1/clients?limit=200&offset=0"),
    ]);
    const escape = (value: unknown) => String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const creator = `
      <section class="card" aria-labelledby="assisted-create-title">
        <h2 id="assisted-create-title">Create a manual publishing assignment</h2>
        <p class="muted">Preview the client, destination and instructions first. The assignment is saved only after you confirm it.</p>
        <form id="assisted-create-form" class="grid cols-2">
          <div><label for="assisted-client">Client</label><select id="assisted-client" name="clientId" required><option value="">Choose a client</option>${clients.filter(c => c.status !== "archived").map(c => `<option value="${escape(c.id)}">${escape(c.business_name)}</option>`).join("")}</select></div>
          <div><label for="assisted-platform">Platform or directory</label><input id="assisted-platform" name="platform" maxlength="80" required placeholder="For example, Yelp" /></div>
          <div><label for="assisted-task-type">Task</label><select id="assisted-task-type" name="taskType"><option value="create_profile">Create a profile</option><option value="post">Publish a post</option><option value="update">Update a listing</option></select></div>
          <div><label for="assisted-target">Exact client page to link</label><input id="assisted-target" name="targetUrl" type="url" required placeholder="https://client.example/services/" /></div>
          <div><label for="assisted-due">Due date (optional)</label><input id="assisted-due" name="dueAt" type="date" /></div>
          <div class="cols-2"><label for="assisted-details">Instructions and approved details (optional)</label><textarea id="assisted-details" name="details" rows="3" maxlength="5000" placeholder="Add the approved listing details or post copy."></textarea></div>
          <div><button type="submit">Preview assignment</button></div>
        </form>
        <div id="assisted-create-preview" class="card" hidden aria-live="polite">
          <h3>Review this assignment</h3><p id="assisted-preview-summary"></p>
          <div style="display:flex;gap:8px"><button id="assisted-confirm-create" type="button" hidden>Confirm and save assignment</button><button id="assisted-cancel-create" class="secondary" type="button" hidden>Cancel preview</button></div>
        </div>
      </section>`;

    const queue = cards.length === 0
      ? `<div class="empty">Nothing waiting. This fills up as no-API platforms (Yelp, Google Sites, manufacturer dealer locators) get queued per client.</div>`
      : null;

    const now = Math.floor(Date.now() / 1000);
    const groups = new Map<string, any[]>();
    for (const card of cards) {
      if (!groups.has(card.platform)) groups.set(card.platform, []);
      groups.get(card.platform)!.push(card);
    }

    function renderCard(card: any): string {
      const fields: { label: string; value: string }[] = JSON.parse(card.fields_json ?? "[]");
      const overdue = card.due_at && card.due_at < now;
      return `
        <div class="card" data-card-id="${card.id}" data-search="${(card.business_name + " " + platformLabel(card.platform)).toLowerCase()}">
          <h2>${card.business_name} <span class="pill neutral" style="margin-left:8px">${card.task_type.replace(/_/g, " ")}</span></h2>
          ${card.due_at ? `<p class="${overdue ? "" : "muted"}" style="${overdue ? "color:var(--red)" : ""};font-size:12px;margin-top:-6px">${overdue ? "Overdue since" : "Due"} ${new Date(card.due_at * 1000).toLocaleDateString()}</p>` : ""}
          <div class="grid cols-2">
            ${fields
              .map(
                (f) => `<div>
                <label>${f.label}</label>
                <div style="display:flex;gap:6px">
                  <input readonly value="${f.value.replace(/"/g, "&quot;")}" />
                  <button class="secondary copy-btn" data-value="${f.value.replace(/"/g, "&quot;")}">Copy</button>
                </div>
              </div>`,
              )
              .join("")}
          </div>
          <div class="card-err"></div>
          <div style="display:flex;gap:8px;margin-top:12px">
            <input class="result-url" placeholder="Paste the resulting URL here" style="flex:1" />
            <button class="done-btn">Mark done</button>
            <button class="secondary skip-btn">Skip</button>
          </div>
        </div>`;
    }

    content.innerHTML = creator + (queue ?? `
      ${cards.length > 8 ? `<div class="search-box"><input id="assisted-search" placeholder="Filter by business or platform…" /></div>` : ""}
      ${[...groups.entries()]
        .map(
          ([platform, group]) => `
        <div class="platform-section">
          <div class="section-header"><h3>${platformLabel(platform)}</h3><span class="count">${group.length} waiting</span></div>
          ${group.map(renderCard).join("")}
        </div>`,
        )
        .join("")}
      <div id="assisted-no-match" class="empty" hidden>No task matches that search.</div>
    `);

    const createForm = root.querySelector<HTMLFormElement>("#assisted-create-form")!;
    const previewBox = root.querySelector<HTMLElement>("#assisted-create-preview")!;
    const previewSummary = root.querySelector<HTMLElement>("#assisted-preview-summary")!;
    const confirmCreate = root.querySelector<HTMLButtonElement>("#assisted-confirm-create")!;
    const cancelCreate = root.querySelector<HTMLButtonElement>("#assisted-cancel-create")!;
    let pending: (Record<string, unknown> & { planHash: string }) | null = null;
    createForm.onsubmit = async event => {
      event.preventDefault();
      const form = new FormData(createForm);
      const due = String(form.get("dueAt") ?? "");
      const details = String(form.get("details") ?? "").trim();
      const payload = {
        clientId: String(form.get("clientId") ?? ""),
        platform: String(form.get("platform") ?? "").trim(),
        taskType: String(form.get("taskType") ?? "create_profile"),
        fields: details ? [{ label: "Assignment details", value: details }] : [],
        targetUrl: String(form.get("targetUrl") ?? "").trim(),
        ...(due ? { dueAt: Math.floor(new Date(`${due}T12:00:00`).getTime() / 1000) } : {}),
        idempotencyKey: `assisted-${crypto.randomUUID()}`,
        dryRun: true,
      };
      const previewButton = createForm.querySelector<HTMLButtonElement>("button[type=submit]")!;
      previewButton.disabled = true;
      try {
        const result = await api<{ planHash: string; task: { clientId: string; platform: string; taskType: string; targetUrl: string; dueAt: number | null; fields: { label: string; value: string }[] } }>("/v1/assisted", { method: "POST", body: JSON.stringify(payload) });
        pending = { ...payload, planHash: result.planHash };
        const clientName = clients.find(c => c.id === result.task.clientId)?.business_name ?? result.task.clientId;
        const instructions = result.task.fields.map(field => `${field.label}: ${field.value}`).join(" | ");
        previewSummary.textContent = `${clientName} · ${platformLabel(result.task.platform)} · ${result.task.taskType.replace(/_/g, " ")} · ${result.task.targetUrl}${result.task.dueAt ? ` · due ${new Date(result.task.dueAt * 1000).toLocaleDateString()}` : ""}${instructions ? ` · ${instructions}` : ""}`;
        previewBox.hidden = false;
        confirmCreate.hidden = false;
        cancelCreate.hidden = false;
      } catch (e) {
        previewBox.hidden = false;
        previewSummary.textContent = (e as Error).message;
        confirmCreate.hidden = true;
        cancelCreate.hidden = true;
      } finally { previewButton.disabled = false; }
    };
    const discardStalePreview = () => {
      if (!pending) return;
      pending = null;
      previewBox.hidden = true;
      confirmCreate.hidden = true;
      cancelCreate.hidden = true;
    };
    createForm.addEventListener("input", discardStalePreview);
    createForm.addEventListener("change", discardStalePreview);
    confirmCreate.onclick = async () => {
      if (!pending) return;
      confirmCreate.disabled = true;
      try {
        await api("/v1/assisted", { method: "POST", body: JSON.stringify({ ...pending, dryRun: false, confirm: true }) });
        pending = null;
        await load();
      } catch (e) {
        previewSummary.textContent = `${(e as Error).message} Retry with the same preview, or cancel and start a new one.`;
      } finally { confirmCreate.disabled = false; }
    };
    cancelCreate.onclick = () => { pending = null; previewBox.hidden = true; };

    if (cards.length === 0) return;

    const searchInput = root.querySelector<HTMLInputElement>("#assisted-search");
    if (searchInput) {
      searchInput.oninput = () => {
        const term = searchInput.value.trim().toLowerCase();
        let anyVisible = false;
        content.querySelectorAll<HTMLElement>(".platform-section").forEach((section) => {
          let sectionHasMatch = false;
          section.querySelectorAll<HTMLElement>("[data-card-id]").forEach((cardEl) => {
            const matches = !term || (cardEl.dataset.search ?? "").includes(term);
            cardEl.hidden = !matches;
            if (matches) sectionHasMatch = true;
          });
          section.hidden = !sectionHasMatch;
          if (sectionHasMatch) anyVisible = true;
        });
        root.querySelector<HTMLElement>("#assisted-no-match")!.hidden = anyVisible;
      };
    }

    content.querySelectorAll<HTMLButtonElement>(".copy-btn").forEach((btn) => {
      btn.onclick = () => navigator.clipboard.writeText(btn.dataset.value ?? "");
    });
    content.querySelectorAll<HTMLElement>("[data-card-id]").forEach((cardEl) => {
      const id = cardEl.dataset.cardId!;
      const errEl = cardEl.querySelector(".card-err")!;
      cardEl.querySelector<HTMLButtonElement>(".done-btn")!.onclick = async () => {
        const resultUrl = cardEl.querySelector<HTMLInputElement>(".result-url")!.value.trim();
        try {
          const res = await api<{ verification: any }>(`/v1/assisted/${id}/done`, {
            method: "POST",
            body: JSON.stringify({ resultUrl: resultUrl || undefined }),
          });
          if (resultUrl && res.verification && res.verification.httpStatus !== 200) {
            errEl.innerHTML = `<div class="error-banner">Marked done, but the URL returned HTTP ${res.verification.httpStatus}. Worth double-checking.</div>`;
            setTimeout(load, 2500);
          } else {
            await load();
          }
        } catch (e) {
          errEl.innerHTML = `<div class="error-banner">${(e as Error).message}</div>`;
        }
      };
      cardEl.querySelector<HTMLButtonElement>(".skip-btn")!.onclick = async () => {
        await api(`/v1/assisted/${id}/skip`, { method: "POST", body: JSON.stringify({}) });
        await load();
      };
    });
  }

  await load();
}
